// /**
//  * Parcels details - list
//  */

// React
import React from 'react';

// UI
import {
  createStyles,
  makeStyles,
  Theme,
  withStyles,
} from '@material-ui/core/styles';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Paper,
  IconButton,
} from '@material-ui/core';

// Icon
import Icon from '@mdi/react';
import {
  mdiChevronDown,
  mdiChevronUp,
  mdiClose,
  mdiRefresh,
} from '@mdi/js';

// Helper
import {
  checkMatchMedia,
  translate,
} from '../../utils/helpers';

// Type
import { ItemDetail } from '../parcels/interfaces';
import theme from '../../theme';
import jMoment from 'moment-jalaali';

type Data = ItemDetail;
type Props = {
  data: any;
  cancelItem: (item: any) => void;
  retrylItem: (item: any) => void;
};
type Ord = 'asc' | 'desc';
interface HeadCell {
  id: keyof Data;
  label: string;
}
interface EnhancedTableProps {
  classes: ReturnType<typeof useStyles>;
  onRequestSort: (
    event: React.MouseEvent<unknown>,
    property: keyof Data
  ) => void;
  order: Ord;
  orderBy: string;
  rowCount: number;
}

// Style
const StyledTableCell = withStyles((theme: Theme) =>
  createStyles({
    head: {
      backgroundColor: theme.palette.primary.main,
      color: theme.palette.common.white,
      fontSize: 12,
      whiteSpace: 'nowrap',
      '@media (max-width: 768px)': {
        fontSize: 10,
      },
    },
    body: {
      fontSize: 14,
      whiteSpace: 'nowrap',
    },
  })
)(TableCell);

const StyledTableRow = withStyles((theme: Theme) =>
  createStyles({
    root: {
      '&:nth-of-type(even)': {
        backgroundColor: theme.palette.action.hover,
      },
    },
  })
)(TableRow);
const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      width: '100%',
    },
    paper: {
      width: '100%',
    },
    table: {
      // minWidth: 750,
    },
    head: {
      backgroundColor: theme.palette.primary.main,
      color: theme.palette.common.white,
    },
    textArea: {
      width: '100%',
    },
    visuallyHidden: {
      border: 0,
      clip: 'rect(0 0 0 0)',
      height: 1,
      margin: -1,
      overflow: 'hidden',
      padding: 0,
      position: 'absolute',
      top: 20,
      width: 1,
    },
    container: {
      maxHeight: 540,
      borderRadius: '4px',
    },
    // Fully visible for active icons
    activeSortIcon: {
      opacity: 1,
      color: `${theme.palette.secondary.main}!important`,
    },
    // Half visible for inactive icons
    inactiveSortIcon: {
      opacity: 0.4,
    },
    // Cancel Dialog
    cancelIcon: {
      textAlign: 'center',
      position: 'relative',
      paddingLeft: theme.spacing(8),
      marginBottom: theme.spacing(7),
      '&::before': {
        content: '""',
        position: 'absolute',
        width: '100px',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%,-50%)',
        height: '100px',
        borderRadius: '50%',
        opacity: '0.2',
        backgroundColor: theme.palette.error.main,
      },
    },
    cancelTitle: {
      fontSize: '16px',
      fontWeight: 500,
      textAlign: 'center',
      marginBottom: theme.spacing(3),
    },
    actionsBox: {
      paddingTop: theme.spacing(2),
      paddingRight: theme.spacing(3),
      paddingLeft: theme.spacing(3),
    },
  })
);

function descendingComparator<T>(a: T, b: T, orderBy: keyof T) {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
}

function getComparator<Key extends keyof any>(
  order: Ord,
  orderBy: Key
): (
    a: { [key in Key]: number | string },
    b: { [key in Key]: number | string }
  ) => number {
  return order === 'desc'
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

function stableSort<T>(array: T[], comparator: (a: T, b: T) => number) {
  const stabilizedThis = array.map((el, index) => [el, index] as [T, number]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) return order;
    return a[1] - b[1];
  });
  return stabilizedThis.map(el => el[0]);
}

function EnhancedTableHead(props: EnhancedTableProps) {
  const classes = useStyles();
  const { order, orderBy, onRequestSort } = props;
  const createSortHandler = (property: keyof Data) => (
    event: React.MouseEvent<unknown>
  ) => {
    onRequestSort(event, property);
  };
  let headCells: HeadCell[] = [
    { id: 'id', label: 'شناسه 3PL سفارش' },
    { id: 'orderId', label: 'شناسه سفارش (کد پیگیری)' },
    { id: 'tplTrackingCode', label: 'کدپیگیری کوریر' },
    { id: 'tplCode', label: 'نام 3PL' },
    { id: 'created', label: 'تاریخ ایجاد در 3PL' },
    { id: 'dimensions', label: 'ابعاد (سانتی متر)' },
    { id: 'weight', label: 'وزن (گرم)' },
    { id: 'price', label: 'قیمت (ریال)' },
    { id: 'status', label: 'وضعیت' },
  ];

  return (
    <TableHead>
      <StyledTableRow>
        {headCells.map(headCell => (
          <StyledTableCell
            key={headCell.id}
            align="center"
            sortDirection={orderBy === headCell.id ? order : false}
            data-cy={headCell.id}
          >
            <TableSortLabel
              active={orderBy === headCell.id}
              direction={orderBy === headCell.id ? order : 'asc'}
              onClick={createSortHandler(headCell.id)}
              classes={{
                // Override with the active class if this is the selected column or inactive otherwise
                icon:
                  orderBy === headCell.id
                    ? classes.activeSortIcon
                    : classes.inactiveSortIcon,
              }}
              IconComponent={() => (
                <Icon
                  path={
                    orderBy === headCell.id && order === 'desc'
                      ? mdiChevronDown
                      : mdiChevronUp
                  }
                  size={0.7}
                  className={
                    orderBy === headCell.id
                      ? classes.activeSortIcon
                      : classes.inactiveSortIcon
                  }
                />
              )}
            >
              {headCell.label}
            </TableSortLabel>
          </StyledTableCell>
        ))}
        <StyledTableCell />
      </StyledTableRow>
    </TableHead>
  );
}

export default function EnhancedTable(props: Props) {
  const classes = useStyles();
  const [order, setOrder] = React.useState<Ord>('desc');
  const [orderBy, setOrderBy] = React.useState<keyof Data>('id');
  // const [dense] = React.useState(false);

  const handleRequestSort = (
    event: React.MouseEvent<unknown>,
    property: keyof Data
  ) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  return (
    <div className={classes.root}>
      <Paper className={classes.paper}>
        <TableContainer className={classes.container}>
          <Table
            stickyHeader
            className={classes.table}
            aria-labelledby="tableTitle"
            size={checkMatchMedia('(max-width: 1024px)') ? 'small' : 'medium'}
            aria-label="enhanced table"
          >
            <EnhancedTableHead
              classes={classes}
              order={order}
              orderBy={orderBy}
              onRequestSort={handleRequestSort}
              rowCount={props.data.length}
            />

            <TableBody>
              {stableSort(props.data, getComparator(order, orderBy))
                .slice()
                .map((row: any, index: number) => {
                  const labelId = `enhanced-table-checkbox-${index}`;

                  return (
                    <StyledTableRow key={index} hover tabIndex={-1}>
                      <TableCell id={labelId} scope="row" align="center">
                        {row.id}
                      </TableCell>
                      <TableCell id={labelId} scope="row" align="center">
                        {row.orderId} - {row.trackingCode}
                      </TableCell>
                      <TableCell id={labelId} scope="row" align="center">
                        {row.tplTrackingCode}
                      </TableCell>
                      <TableCell id={labelId} scope="row" align="center">
                        {translate(row.tplCode, "TPL")}
                      </TableCell>
                      <TableCell
                        id={labelId}
                        scope="row"
                        dir="ltr"
                        align="center"
                      >
                        {row.created &&
                          jMoment(row.created, 'YYYY/M/D HH:mm:ss.SSZ')
                            .locale('fa')
                            .format('jYYYY/jMM/jDD HH:mm:ss')}
                      </TableCell>
                      <TableCell
                        style={{ direction: 'ltr' }}
                        id={labelId}
                        scope="row"
                        align="center"
                      >
                        {row.length
                          ? `${row.length} x ${row.width} x ${row.height}`
                          : '-'}
                      </TableCell>
                      <TableCell id={labelId} scope="row" align="center">
                        {row.weight}
                      </TableCell>
                      <TableCell align="center" data-cy="row-id">
                        {row.price}
                      </TableCell>
                      <TableCell
                        align="center"
                        data-cy="row-id"
                        style={{
                          color:
                            row.status === 'REGISTERED_IN_3PL' ||
                              row.status === 'DELIVERED_TO_3PL' ||
                              row.status === 'DELIVERED_TO_3PL_COURIER' ||
                              row.status === 'DELIVERED'
                              ? theme.palette.success.main
                              : theme.palette.error.main,
                        }}
                      >
                        {translate(row.status, 'tplStatus')}
                      </TableCell>
                      <TableCell>
                        {row.tplCode !== 'ALOPEYK' &&
                          row.status !== 'CANCELLED' && (
                            <IconButton
                              aria-label="cancel"
                              title="لغو آیتم "
                              size="small"
                              onClick={() => props.cancelItem(row)}
                            >
                              <Icon
                                path={mdiClose}
                                size={1}
                                color={theme.palette.error.main}
                              />
                            </IconButton>
                          )}
                        {row.tplCode !== 'ALOPEYK' &&
                          row.status === 'NOT_REGISTERED' && (
                            <IconButton
                              aria-label="retry"
                              title="ارسال دوباره سفارش "
                              size="small"
                              onClick={() => props.retrylItem(row)}
                            >
                              <Icon
                                path={mdiRefresh}
                                size={1}
                                color={theme.palette.gray.main}
                              />
                            </IconButton>
                          )}
                      </TableCell>
                    </StyledTableRow>
                  );
                })}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </div>
  );
}
